import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// tslint:disable-next-line:import-blacklist
import { CookieService } from './cookie.service';
import { finalize, map } from 'rxjs/operators';
import { Error } from '../enums/error.enum';
import { LogOffOptions } from './log-off-options.model';
import { Observable } from 'rxjs/internal/Observable';
import { of } from 'rxjs/internal/observable/of';
import { environment } from '@environment';
import { AppInsightsService } from '../../typescript/services/app-insights.service';
import { firstValueFrom } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    community: string;
    isLoggingOut;
    needToRefreshToken;
    redirectUrl: string;
    refreshPromise;
    signalRToken: string;
    token: string;

    private readonly communityKey;
    private readonly expiresKey;
    private iframeCommunicator;
    private readonly refreshTokenKey: string;
    private timeDifference = 0;
    private readonly timeDifferenceKey: string;
    private tokenExpires: Date;

    constructor(
        private httpClient: HttpClient,
        private cookieService: CookieService,
        private appInsightsService: AppInsightsService,
        @Inject(PLATFORM_ID) private platformId: string,
    ) {
        this.refreshTokenKey = environment.environment + '_refresh_token';
        this.expiresKey = environment.environment + '_access_token_expires';
        this.communityKey = environment.environment + '_community';

        this.tokenExpires = new Date(this.cookieService.getCookie(this.expiresKey));

        this.timeDifferenceKey = environment.environment + '_timeDifference';
        this.timeDifference = +this.cookieService.getCookie(this.timeDifferenceKey);
        if (isNaN(this.timeDifference)) {
            this.timeDifference = 0;
        }

        this.community = this.getCommunityFromUrl() || this.getCommunityFromCookies();

        if (!environment.isNoNeedInCommunicator) {
            this.initCommunicator();
        }
    }

    getAuthorizationHeader(communityName, isStayOnCurrentUrlAfterLogOut = false, source?: string): Promise<any> {
        if (!communityName || communityName === 'null') {
            this.appInsightsService.logEvent('EmptyCommunity', {
                source,
                communityFromUrl: this.getCommunityFromUrl(),
            });

            communityName = this.getCommunityFromUrl();
        }

        if (!this.getRefreshToken()) {
            this.appInsightsService.logEvent('Logoff', {
                reason: 'refresh token is empty',
                file: 'authentication.service.ts',
            });
            this.logOff({ isStayOnCurrentUrl: isStayOnCurrentUrlAfterLogOut, isNeedRedirect: true }); // ?
            return firstValueFrom(of(''));
        }

        if (!this.token || this.isTokenExpired() || this.needToRefreshToken) {
            if (!this.refreshPromise) {
                this.refreshPromise = firstValueFrom(this.refreshToken(communityName));
            }

            return this.refreshPromise.then(
                (data) => {
                    return firstValueFrom(of(data));
                },
                (response) => {
                    if (response.error && response.error.errorId === Error.authorizationInfoIsBroken) {
                        const refreshTokenFromCookies = this.getRefreshToken();
                        const refreshTokenFromRequest = response.error.debugInfo.token;

                        if (
                            refreshTokenFromCookies &&
                            refreshTokenFromRequest &&
                            refreshTokenFromRequest !== refreshTokenFromCookies &&
                            this.token
                        ) {
                            const header = 'Bearer ' + this.token;

                            return firstValueFrom(of(header));
                        } else {
                            this.appInsightsService.logEvent('Logoff', {
                                reason: 'Error.authorizationInfoIsBroken',
                                file: 'authentication.service.ts',
                                refreshToken: !!this.getRefreshToken(),
                            });
                            this.logOff({ isStayOnCurrentUrl: isStayOnCurrentUrlAfterLogOut });
                        }
                    }

                    return firstValueFrom(of(response));
                },
            );
        } else {
            const header = 'Bearer ' + this.token;

            return firstValueFrom(of(header));
        }
    }

    getRefreshToken(): string {
        return this.cookieService.getCookie(this.refreshTokenKey);
    }

    isLoggedIn(): boolean {
        return Boolean(this.getRefreshToken());
    }

    logOff(options: LogOffOptions = {}) {
        if (this.isLoggingOut) {
            return;
        }
        this.isLoggingOut = true;
        if (options.isNeedToRevokeToken && this.getRefreshToken()) {
            const body = new URLSearchParams();
            body.set('token', this.getRefreshToken());

            const requestOptions = {
                headers: new HttpHeaders({
                    'Content-Type': 'application/x-www-form-urlencoded',
                }),
                withCredentials: true,
            };

            this.httpClient
                .post<any>(environment.apiAuthUrl + 'oauth/revoke/', body.toString(), requestOptions)
                .pipe(
                    finalize(() => {
                        this.logOffCallback(options);
                    }),
                )
                .subscribe();
        } else {
            this.logOffCallback(options);
        }
    }

    redirectToCommunities() {
        window.location.href = environment.loginAppUrl + '/communities';
    }

    refreshToken(communityName: string): Observable<string> {
        const refreshToken = this.getRefreshToken();

        const body = new URLSearchParams();
        body.set('refresh_token', refreshToken);
        body.set('grant_type', 'refresh_token');
        body.set('client_id', 'webApp');

        const options = {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded').delete('DNT'),
            withCredentials: true,
        };

        return this.httpClient
            .post<any>(environment.apiAuthUrl + 'oauth/token?community=' + communityName, body.toString(), options)
            .pipe(
                map((data) => {
                    data['communityName'] = communityName;

                    this.updateAuthData(data);

                    this.refreshPromise = null;

                    this.needToRefreshToken = false;

                    return 'Bearer ' + data['access_token'];
                }),
            );
    }

    // return difference in milliseconds between client and server time
    setTimeDifference(dateString): void {
        if (dateString) {
            this.timeDifference = new Date().getTime() - new Date(dateString).getTime();
            this.cookieService.setCookie(this.timeDifferenceKey, this.timeDifference);
        }
    }

    private getCommunityFromCookies() {
        const community = this.cookieService.getCookie(this.communityKey);

        return this.getProcessedCommunityName(community);
    }

    private getCommunityFromUrl() {
        let community;

        try {
            const communityNameRegex = '[a-zA-Z0-9_]{2,}';
            community = new RegExp(communityNameRegex).exec(window.location.pathname)[0];
        } catch {}

        return this.getProcessedCommunityName(community);
    }

    private getProcessedCommunityName(community): string {
        if (community) {
            community = community.toLowerCase();
        }

        if (
            community &&
            (community === 'login' ||
                community === 'communities' ||
                community === 'forget' ||
                community === 'facebooksb' ||
                community === 'linkedinsb' ||
                community === 'trustpilotsb' ||
                community === 'googlesb' ||
                community === 'youtubesb' ||
                community === 'microsoftdynamicssb' ||
                community === 'facebookinstallation' ||
                community === 'instagraminstallation' ||
                community === 'tiktokinstallation' ||
                community === 'linkedininstallation' ||
                community === 'trustpilotinstallation' ||
                community === 'googleinstallation' ||
                community === 'youtubeinstallation')
        ) {
            return '';
        }

        if (community) {
            community = community.toLowerCase();
        }

        return community || '';
    }

    private getTokenExpiresDate(): Date {
        return this.tokenExpires;
    }

    private initCommunicator() {
        if (!isPlatformBrowser(this.platformId)) return;
        const iframeSource = environment.loginAppUrl + 'assets/auth-communicator/communicator.html';
        // Create the iframe
        const iframe = document.createElement('iframe');
        iframe.setAttribute('src', iframeSource);
        iframe.setAttribute('id', 'iframe-communicator');
        iframe.setAttribute('style', 'display: none;');
        document.body.appendChild(iframe);
        // Send a message to the child iframe
        this.iframeCommunicator = document.getElementById('iframe-communicator');

        this.onMessage = this.onMessage.bind(this);
        window.addEventListener('message', this.onMessage);
    }

    private isTokenExpired(): boolean {
        const minute = 60000;
        return this.getTokenExpiresDate().getTime() - new Date().getTime() + this.timeDifference < minute;
    }

    private logOffCallback(options: LogOffOptions = {}) {
        if (!isPlatformBrowser(this.platformId)) return;
        const redirectUrl = encodeURIComponent(options.redirectUrl || window.location.href);

        this.cookieService.deleteCookie(this.expiresKey);
        this.cookieService.deleteCookie(this.communityKey);
        this.cookieService.deleteCookie(this.refreshTokenKey);
        this.cookieService.deleteCookie(this.timeDifferenceKey);

        // todo: check why do we need it to fix tests
        options = JSON.parse(JSON.stringify(options)); // Fix tests

        if (!options.isNotSendEventToOtherTabs) {
            this.iframeCommunicator['contentWindow'].postMessage({ options, isLogOffOptions: true }, '*');
        }

        setTimeout(() => {
            if (!options.isStayOnCurrentUrl) {
                if (options.isNeedRedirect) {
                    let postfix = '';

                    if (options.isExpired) {
                        postfix = 'isExpired=true';
                    }

                    if (this.community || options.redirectUrl) {
                        window.location.href = environment.loginAppUrl + '?url=' + redirectUrl + '&' + postfix;
                    } else {
                        window.location.href = environment.loginAppUrl + (postfix ? '?' + postfix : '');
                    }
                } else {
                    window.location.href = environment.loginAppUrl;
                }
            }

            if (options.callback) {
                options.callback();
            }

            this.refreshPromise = null;
        }, 50);
    }

    private onMessage(event: MessageEvent) {
        if (environment.loginAppUrl.indexOf(event.origin) === 0 && event.data?.isLogOffOptions) {
            this.appInsightsService.logEvent('Logoff', {
                reason: 'logout event from communicator',
                file: 'authentication.service.ts',
                refreshToken: !!this.getRefreshToken(),
            });

            event.data.options.isNotSendEventToOtherTabs = true;
            window.removeEventListener('message', this.onMessage);
            this.logOff(event.data.options);
        }
    }

    private updateAuthData(data): void {
        this.cookieService.setCookie(this.refreshTokenKey, data['refresh_token']);
        this.cookieService.setCookie(this.expiresKey, data['.expires']);
        this.cookieService.setCookie(this.communityKey, data['communityName']);

        this.token = data['access_token'];
        this.signalRToken = data['signalr_token'];
        this.tokenExpires = new Date(this.cookieService.getCookie(this.expiresKey));
    }
}
